import { useState, useEffect } from 'react';
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useParams } from "react-router";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import * as theme from '../../theme';
import { updateOnboardingStep } from '../../functions/updateOnboarding';
import Heading from '../../components/Heading';
import TrainingContentList from '../../components/dashboards/lists/TrainingContentList';
import VideoPlayer from '../../components/VideoPlayer';
import { Box, Button, CircularProgress } from '@mui/material';
import TrainingContentContext from '../../contexts/TrainingContentContext';
import DocumentViewer from '../../components/DocumentViewer';

export default function Training() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [practitioner, setPractitioner] = useState({});
  const [activeContent, setActiveContent] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { practitionerId } = useParams();

  useEffect(() => {
    if (currentUser.role === 'practitioner' && practitionerId != null) {
      getPractitioner();
    } else {
      setPractitioner({});
    }
  }, [practitionerId, currentUser]);

  useEffect(() => {
    if (activeContent) {
      window.scrollTo(0, 0);
    }
  }, [activeContent]);

  useEffect(() => {
    if (!activeContent) {
      if (practitioner.training) {
        // Find the first uncompleted training item
        const firstUncompleted = TrainingContentContext.find(
          (item) => !practitioner.training[item.action]?.dateCompleted
        );
        setActiveContent(firstUncompleted || TrainingContentContext[0]);
      } else {
        setActiveContent(TrainingContentContext[0]);
      }
    }
  }, [practitioner, activeContent]);

  async function getPractitioner() {
    try {
      const practitionerRef = doc(db, "practitioners", practitionerId);
      const docSnap = await getDoc(practitionerRef);
      setPractitioner(docSnap.data() || {});
    } catch (err) {
      console.log("Failed to get practitioner: " + err.message);
      setError("Failed to get practitioner: " + err.message);
    }
  }

  const handleComplete = async (action) => {
    if (currentUser.role !== 'practitioner' || !practitionerId) return;

    try {
      setIsLoading(true);

      const { success, practitioner } = await updateOnboardingStep({
        practitionerId,
        step: 'training',
        trainingAction: action,
      });

      if (success) {
        // Update local state
        setPractitioner(practitioner);

        // Find next uncompleted item
        const currentIndex = TrainingContentContext.findIndex((item) => item.action === action);
        const nextUncompleted = TrainingContentContext.slice(currentIndex + 1).find(
          (item) => !practitioner.training[item.action]?.dateCompleted
        );
        setActiveContent(nextUncompleted || null);
      } else {
        throw new Error('Failed to update training');
      }
    } catch (err) {
      console.error("Failed to mark video as complete: ", err.message);
      setError("Failed to mark video as complete: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (activeContent) {
      handleComplete(activeContent.action);
    }
  };

  return (
    <div style={theme.Container.flexColumn}>
      <div style={{ ...theme.Container.flexColumn, marginBottom: 10 }}>
        <Heading title={`Training`} onClick={() => navigate(`/dashboard`)} />
        {error && (
          <div style={{ ...theme.Error, marginTop: 10, textAlign: 'center' }}>
            {error}
          </div>
        )}
      </div>
      <div style={theme.Container.fullMax}>
        <div style={theme.Container.flexOne}>
          <TrainingContentList
            practitionerId={practitionerId}
            training={practitioner.training ? practitioner.training : {}}
            currentUser={currentUser}
            setError={setError}
            setActiveContent={setActiveContent}
            onComplete={handleComplete}
          />
        </div>
        <div style={theme.Container.flexTwo}>
          <div style={{ ...theme.Container.flexRow, width: '100%', gap: 20 }}>
            <div style={{ ...theme.Container.flexColumn, flex: 1, gap: 10 }}>
              {activeContent && activeContent.type === 'video' && (
                <>
                  <VideoPlayer videoId={activeContent.title} isPublic={false} />
                  <Heading
                    title={activeContent ? activeContent.title : 'Select a Training Item'}
                    subHeading={true}
                    center={true}
                  />
                </>
              )}
              {activeContent && activeContent.type === 'multi' && (
                activeContent.component
              )}
              {activeContent && activeContent.type === 'report' && (
                <>
                  <Heading
                    title={activeContent ? activeContent.title : 'Select a Training Item'}
                    subHeading={true}
                    center={true}
                  />
                  <DocumentViewer documentId={activeContent ? activeContent.title : null} description={activeContent ? activeContent.description : null} />
                </>
              )}
              {activeContent && !practitioner.training?.[activeContent.action]?.dateCompleted && currentUser.role === 'practitioner' && (
                <Box sx={{ display: "flex", width: '100%', gap: 2, marginTop: 4, alignItems: 'center', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    startIcon={isLoading && <CircularProgress size={20} color="inherit" />}
                  >
                    {isLoading ? 'Processing...' : 'Mark Complete'}
                  </Button>
                </Box>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}